import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { overlayBackdropStyle } from '../OverlayPanel';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { ProductCard } from '../products';
import { Input, Button } from '../design-system';
import { apiService } from '../../services/api';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { withAlpha } from '../../theme';

export interface ProductSelectorModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (selectedProductIds: string[]) => void;
  excludeCatalogueId?: string; // Exclude products already in this catalogue
  excludeVendorId?: string; // Exclude products already supplied by this vendor
  businessId?: string;
  title?: string;
  confirmText?: string;
  allowMultiple?: boolean;
  /** Shows a "+ Create New Product" affordance that creates a catalogue-less
   * product (no catalogue forced — see product.controller.ts's 'default'
   * exemption) with this vendor pre-linked via vendorIds, then auto-selects
   * it. Only meaningful alongside excludeVendorId — "create and link to this
   * vendor" is the concrete use case, not a generic product-create shortcut. */
  allowCreateForVendorId?: string;
}

export const ProductSelectorModal: React.FC<ProductSelectorModalProps> = ({
  visible,
  onClose,
  onConfirm,
  excludeCatalogueId,
  excludeVendorId,
  businessId,
  title,
  confirmText,
  allowMultiple = true,
  allowCreateForVendorId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductPrice, setNewProductPrice] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (visible) {
      loadProducts(0, true);
      setSelectedProductIds(new Set());
      setSearchQuery('');
      setShowCreateForm(false);
      setNewProductName('');
      setNewProductPrice('');
    }
  }, [visible, excludeCatalogueId, excludeVendorId]);

  const loadProducts = useCallback(async (page: number = 0, reset: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
      }

      // Load all products (using "default" to get all products for the business)
      const response = await apiService.getProductsByCatalogue('default', {
        limit: 20,
        skip: page * 20,
        search: searchQuery || undefined,
      });

      if (response.statusCode === 1000 && response.data) {
        const newProducts = response.data.products || [];
        
        // Filter out products already linked via whichever exclusion is active.
        let filteredProducts = newProducts;
        if (excludeCatalogueId && excludeCatalogueId !== 'default') {
          filteredProducts = filteredProducts.filter(
            (p: any) => !(p.catalogueIds || []).includes(excludeCatalogueId)
          );
        }
        if (excludeVendorId) {
          filteredProducts = filteredProducts.filter(
            (p: any) => !(p.vendorIds || []).includes(excludeVendorId)
          );
        }

        setProducts((prev) => (reset ? filteredProducts : [...prev, ...filteredProducts]));
        setHasMore(newProducts.length === 20);
        setCurrentPage(page);
      }
    } catch (error: any) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }, [excludeCatalogueId, excludeVendorId, searchQuery]);

  const handleCreateProduct = async () => {
    if (!newProductName.trim() || !newProductPrice.trim() || !allowCreateForVendorId) return;
    const price = Number(newProductPrice);
    if (!Number.isFinite(price) || price < 0) return;

    try {
      setCreating(true);
      const response = await apiService.createProduct('default', {
        name: newProductName.trim(),
        price,
        currency: 'USD',
        vendorIds: [allowCreateForVendorId],
      });
      if (response.statusCode === 1000 && response.data) {
        const created = response.data.product || response.data;
        setProducts((prev) => [created, ...prev]);
        setSelectedProductIds((prev) => {
          const next = allowMultiple ? new Set(prev) : new Set<string>();
          next.add(created._id);
          return next;
        });
        setShowCreateForm(false);
        setNewProductName('');
        setNewProductPrice('');
      }
    } catch (error: any) {
      console.error('Error creating product:', error);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (visible && searchQuery !== undefined) {
      // Debounce search
      const timer = setTimeout(() => {
        loadProducts(0, true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, visible]);

  const handleProductToggle = (productId: string) => {
    if (!allowMultiple) {
      setSelectedProductIds(new Set([productId]));
      return;
    }

    setSelectedProductIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    if (selectedProductIds.size === 0) {
      return;
    }
    onConfirm(Array.from(selectedProductIds));
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadProducts(currentPage + 1, false);
    }
  };

  const renderProduct = ({ item }: { item: any }) => {
    const isSelected = selectedProductIds.has(item._id);
    
    return (
      <TouchableOpacity
        style={[styles.productItem, { flexDirection: rtl.rowDirection }, isSelected && styles.productItemSelected]}
        onPress={() => handleProductToggle(item._id)}
        activeOpacity={0.7}
      >
        <View style={[styles.productCheckbox, rtl.marginEnd(spacing.sm)]}>
          {isSelected && (
            <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
          )}
          {!isSelected && (
            <Ionicons name="ellipse-outline" size={24} color={colors.border} />
          )}
        </View>
        <View style={styles.productContent}>
          <ProductCard
            product={item}
            showActions={false}
            containerStyle={styles.productCard}
          />
        </View>
      </TouchableOpacity>
    );
  };

  const renderContent = () => (
    <>
      {/* Header */}
        <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.headerLeft, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>{title ?? t('productSelector.title')}</Text>
          </View>
          {selectedProductIds.size > 0 && (
            <Text style={styles.selectedCount}>
              {t('productSelector.selectedCount', { count: selectedProductIds.size })}
            </Text>
          )}
        </View>

        {/* Search */}
        <View style={styles.searchContainer}>
          <View style={[styles.searchInputContainer, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="search" size={20} color={colors.textLight} />
            <Input
              placeholder={t('productSelector.searchPlaceholder')}
              value={searchQuery}
              onChangeText={setSearchQuery}
              containerStyle={styles.searchInput}
            />
          </View>
        </View>

        {/* Create New Product — only offered when the caller is linking to a
            specific vendor (allowCreateForVendorId); creates a catalogue-less
            product with this vendor pre-linked and auto-selects it. */}
        {allowCreateForVendorId && (
          <View style={styles.createSection}>
            {!showCreateForm ? (
              <TouchableOpacity style={[styles.createToggle, { flexDirection: rtl.rowDirection }]} onPress={() => setShowCreateForm(true)}>
                <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                <Text style={styles.createToggleText}>{t('productSelector.createNewProduct')}</Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.createForm}>
                <Input
                  placeholder={t('productSelector.productNamePlaceholder')}
                  value={newProductName}
                  onChangeText={setNewProductName}
                  containerStyle={styles.createInput}
                />
                <Input
                  placeholder={t('productSelector.pricePlaceholder')}
                  value={newProductPrice}
                  onChangeText={setNewProductPrice}
                  keyboardType="numeric"
                  containerStyle={styles.createInput}
                />
                <View style={[styles.createFormActions, { flexDirection: rtl.rowDirection }]}>
                  <Button title={t('common.cancel')} onPress={() => setShowCreateForm(false)} variant="outline" style={styles.createFormButton} />
                  <Button
                    title={creating ? t('productSelector.creating') : t('productSelector.createAndSelect')}
                    onPress={handleCreateProduct}
                    variant="primary"
                    disabled={creating || !newProductName.trim() || !newProductPrice.trim()}
                    style={styles.createFormButton}
                  />
                </View>
              </View>
            )}
          </View>
        )}

        {/* Products List */}
        {loading && products.length === 0 ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>{t('productSelector.loadingProducts')}</Text>
          </View>
        ) : products.length === 0 ? (
          <View style={styles.centerContainer}>
            <Ionicons name="cube-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('productSelector.noProductsFound')}</Text>
            <Text style={styles.emptySubtext}>
              {searchQuery ? t('productSelector.tryDifferentSearch') : t('productSelector.createProductsFirst')}
            </Text>
          </View>
        ) : (
          <FlatList
            data={products}
            renderItem={renderProduct}
            keyExtractor={(item) => item._id}
            contentContainerStyle={styles.listContent}
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.5}
            ListFooterComponent={
              loading && products.length > 0 ? (
                <View style={styles.footer}>
                  <ActivityIndicator size="small" color={colors.primary} />
                </View>
              ) : null
            }
          />
        )}

        {/* Footer Actions */}
        <View style={[styles.footerActions, { flexDirection: rtl.rowDirection }]}>
          <Button
            title={t('common.cancel')}
            onPress={onClose}
            variant="outline"
            style={styles.cancelButton}
          />
          <Button
            title={confirmText ?? t('productSelector.addSelected')}
            onPress={handleConfirm}
            variant="primary"
            disabled={selectedProductIds.size === 0}
            style={styles.confirmButton}
          />
        </View>
    </>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
      transparent={Platform.OS === 'web'}
    >
      {Platform.OS === 'web' ? (
        <View style={styles.webOverlay}>
          <TouchableOpacity
            style={styles.webBackdrop}
            activeOpacity={1}
            onPress={onClose}
          />
          <View style={styles.webContainer}>
            {renderContent()}
          </View>
        </View>
      ) : (
        <View style={styles.container}>
          {renderContent()}
        </View>
      )}
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  // Full-viewport centered overlay — works regardless of whether the
  // left/right desktop panes are showing (they collapse on narrow/mobile
  // viewports), unlike the previous fixed left:355/right:355 offsets which
  // assumed a permanent 3-pane desktop layout and pushed the modal fully
  // off-screen on a phone-width browser. Mirrors MapLocationPicker's
  // webOverlay/webContainer pattern exactly.
  webOverlay: {
    ...Platform.select({
      web: {
        position: 'fixed' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9998,
        display: 'flex' as any,
        alignItems: 'center',
        justifyContent: 'center',
        ...overlayBackdropStyle(),
      },
      default: { flex: 1 },
    }),
  } as any,
  webBackdrop: {
    ...Platform.select({
      web: {
        position: 'absolute' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
      },
      default: {},
    }),
  } as any,
  webContainer: {
    ...Platform.select({
      web: {
        position: 'relative' as any,
        width: '100%' as any,
        maxWidth: 600,
        height: '90%' as any,
        maxHeight: 700,
        backgroundColor: colors.background.surface,
        zIndex: 2,
        borderRadius: designTokens.borderRadius.lg,
        overflow: 'hidden',
        ...designTokens.shadows.lg,
      },
      default: {},
    }),
  } as any,
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...(Platform.OS === 'web' && {
      position: 'sticky',
      top: 0,
      backgroundColor: colors.background.surface,
      zIndex: 10,
    }),
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  closeButton: {
    padding: spacing.xs,
    marginRight: spacing.sm,
  },
  headerTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  selectedCount: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  searchContainer: {
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  searchInputContainer: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  searchInput: {
    flex: 1,
    marginBottom: 0,
  },
  listContent: {
    padding: spacing.md,
  },
  createSection: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  createToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  createToggleText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  createForm: {
    gap: spacing.sm,
    paddingBottom: spacing.sm,
  },
  createInput: {
    marginBottom: 0,
  },
  createFormActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  createFormButton: {
    flex: 1,
  },
  productItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    padding: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  productItemSelected: {
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.06),
  },
  productCheckbox: {
  },
  productContent: {
    flex: 1,
  },
  productCard: {
    marginBottom: 0,
    shadowOpacity: 0,
    elevation: 0,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  emptyText: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.md,
  },
  emptySubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs,
  },
  footer: {
    padding: spacing.md,
    alignItems: 'center',
  },
  footerActions: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background.surface,
    ...(Platform.OS === 'web' && {
      position: 'sticky',
      bottom: 0,
    }),
  },
  cancelButton: {
    flex: 1,
  },
  confirmButton: {
    flex: 1,
  },
});

